import AppTableContainer from '@nxsys/components/AppTableContainer';
import useAEPNSocket from '@nxsys/hooks/useAEPNSocket';
import { AppDispatch, AppState } from '@nxsys/store';
import { aepnActions } from '@nxsys/store/slices/aepn/reducer/aepnSlice';
import { IAepnUpload } from '@nxsys/types/models/aepn/upload';
import { showToast } from '@nxsys/utils/toast';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UploadAEPNModal from '../components/UploadAEPNModal';
import UploadDetailModal from '../components/UploadDetailModal';
import UploadResultModal from '../components/UploadResultModal';
import { getUploadColumns } from './upload-columns';

type AEPNUploadTabProps = {
  isUploadModalOpen?: boolean;
  onUploadModalClose?: () => void;
  taxYear?: number;
};

const PAGE_SIZE = 20;

const AEPNUploadTab: React.FC<AEPNUploadTabProps> = ({
  isUploadModalOpen = false,
  onUploadModalClose,
  taxYear,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: AppState) => state.appStore);
  const { uploadHistory } = useSelector((state: AppState) => state.aepnStore);
  const { loading, list, totalDocs } = uploadHistory;

  const [page, setPage] = useState(0);
  const uploadModal = isUploadModalOpen;
  const [resultModal, setResultModal] = useState<{ open: boolean; data: any | null }>({ open: false, data: null });
  const [detailModal, setDetailModal] = useState<{ open: boolean; data: any | null }>({ open: false, data: null });

  const fetchHistory = useCallback(
    (pageNum = 0) => {
      dispatch(aepnActions.getUploadHistory({ page: pageNum, size: PAGE_SIZE, taxYear }));
    },
    [dispatch, taxYear],
  );

  useEffect(() => {
    fetchHistory(page);
  }, [page, taxYear]);

  useAEPNSocket({
    eventName: `naersa-aepn-save-uploaded-${user?.id}`,
    onSuccess: () => {
      showToast({ type: 'success', message: 'Upload AEPN success!' });
      fetchHistory(0);
      setPage(0);
    },
    onError: (err) => {
      showToast({ type: 'error', message: err || 'Upload AEPN failed' });
    },
  });

  const handleUploadSuccess = (data: any[]) => {
    if (data.length > 0) {
      setResultModal({ open: true, data: data[0] });
    }
  };

  const handleViewDetail = async (record: IAepnUpload) => {
    try {
      const res = await dispatch(aepnActions.getUploadHistoryById(record.id)).unwrap();
      setDetailModal({ open: true, data: res?.data ?? res });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || 'Failed to load detail' });
    }
  };

  const onChangePagination = (newPage: number, _pageSize: number) => {
    setPage(newPage - 1);
  };

  const columns = getUploadColumns({ onClickView: handleViewDetail });

  return (
    <>
      <AppTableContainer
        rowKey="id"
        data={list ?? []}
        loading={loading}
        page={page}
        total={totalDocs}
        pageSize={PAGE_SIZE}
        columns={columns}
        scroll={{ x: 1800, y: 600 }}
        onChangePagination={onChangePagination}
        rowClassName={(record: any) => (record?.errorMessage ? 'row-error' : '')}
      />

      <UploadAEPNModal
        isOpen={uploadModal}
        toggle={() => onUploadModalClose?.()}
        onUploadSuccess={handleUploadSuccess}
      />

      <UploadResultModal
        isOpen={resultModal.open}
        toggle={() => setResultModal({ open: false, data: null })}
        uploadData={resultModal.data}
        onSaveSuccess={() => {
          fetchHistory(0);
          setPage(0);
        }}
      />

      <UploadDetailModal
        isOpen={detailModal.open}
        toggle={() => setDetailModal({ open: false, data: null })}
        data={detailModal.data}
      />
    </>
  );
};

export default AEPNUploadTab;
